import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Paper, 
  LinearProgress,
  Tab,
  Tabs,
  Fade,
  useMediaQuery,
  Button,
  TextField,
  Alert,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Avatar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Switch,
  FormControlLabel,
  Divider
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Business as BusinessIcon,
  Star as StarIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  SwapHoriz as SwitchIcon,
  AccountCircle as AccountIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import client from '../../API';
import useAuth from '../../Hooks/use-auth';
import { useCreatorAuth } from '../../Hooks/creator-use-auth';
import { replaceConsoleError } from '../../Utils/toastNotifications';
import { ThemeProvider, createTheme } from '@mui/material/styles';

// Force light theme like TrophyRoom
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
    text: {
      primary: '#212121',
      secondary: '#757575',
    },
    success: {
      main: '#388e3c',
      light: '#66bb6a',
      dark: '#2e7d32',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }
      }
    }
  }
});

// Username availability checker component
const UsernameChecker = ({ username, onUsernameChange, onAvailabilityChange }) => {
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (!username || username.length < 3) {
      setAvailable(null);
      setSuggestions([]);
      onAvailabilityChange(false);
      return;
    }

    const timer = setTimeout(async () => {
      setChecking(true);
      try {
        const result = await client.accountLinking.checkUsername(username);
        setAvailable(result.available);
        setSuggestions(result.suggestions || []);
        onAvailabilityChange(result.available);
      } catch (error) {
        replaceConsoleError('Username check failed', error);
        setAvailable(false);
        onAvailabilityChange(false);
      } finally {
        setChecking(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [username, onAvailabilityChange]);

  return (
    <Box>
      <TextField
        fullWidth
        label="Creator Username"
        value={username}
        onChange={(e) => onUsernameChange(e.target.value)}
        placeholder="yourhandle"
        InputProps={{
          startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>@</Typography>,
          endAdornment: checking ? (
            <CircularProgress size={20} />
          ) : available === true ? (
            <CheckIcon color="success" />
          ) : available === false ? (
            <CloseIcon color="error" />
          ) : null
        }}
        helperText={
          available === false
            ? "Username not available"
            : available === true
            ? "Username available!"
            : "3-33 characters, letters, numbers, and underscores only"
        }
        error={available === false}
        sx={{ mb: 2 }}
      />

      {suggestions.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Suggestions:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {suggestions.map((suggestion) => (
              <Chip
                key={suggestion}
                label={`@${suggestion}`}
                variant="outlined"
                clickable
                onClick={() => onUsernameChange(suggestion)}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

// Creator account creation form
const CreateCreatorForm = ({ onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    creator_username: '',
    display_name: '',
    email: '',
    creator_data: {
      bio: '',
      location: '',
      tier_level: 'micro',
      gender: '',
      ethnicity: '',
      primary_markets: [],
      tiktok: '',
      instagram: '',
      youtube: '',
      twitter: ''
    }
  });
  const [usernameAvailable, setUsernameAvailable] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleInputChange = (field, value, isCreatorData = false) => {
    if (isCreatorData) {
      setFormData(prev => ({
        ...prev,
        creator_data: {
          ...prev.creator_data,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = () => {
    if (!formData.creator_username || !usernameAvailable) {
      return;
    }
    onSubmit(formData);
  };

  return (
    <Box>
      <UsernameChecker
        username={formData.creator_username}
        onUsernameChange={(value) => handleInputChange('creator_username', value)}
        onAvailabilityChange={setUsernameAvailable}
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Display Name"
            value={formData.display_name}
            onChange={(e) => handleInputChange('display_name', e.target.value)}
            helperText="How your name appears to others"
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            helperText="For creator communications"
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Bio"
            multiline
            rows={3}
            value={formData.creator_data.bio}
            onChange={(e) => handleInputChange('bio', e.target.value, true)}
            placeholder="Tell people about your content and style..."
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Creator Tier</InputLabel>
            <Select
              value={formData.creator_data.tier_level}
              onChange={(e) => handleInputChange('tier_level', e.target.value, true)}
              label="Creator Tier"
            >
              <MenuItem value="micro">Micro (1K-100K followers)</MenuItem>
              <MenuItem value="macro">Macro (100K-1M followers)</MenuItem>
              <MenuItem value="celebrity">Celebrity (1M+ followers)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Location"
            value={formData.creator_data.location}
            onChange={(e) => handleInputChange('location', e.target.value, true)}
            placeholder="City, Country"
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={showAdvanced}
                onChange={(e) => setShowAdvanced(e.target.checked)}
              />
            }
            label="Show social media & advanced options"
          />
        </Grid>

        {showAdvanced && (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="TikTok Handle"
                value={formData.creator_data.tiktok}
                onChange={(e) => handleInputChange('tiktok', e.target.value, true)}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>@</Typography>
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Instagram Handle"
                value={formData.creator_data.instagram}
                onChange={(e) => handleInputChange('instagram', e.target.value, true)}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>@</Typography>
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="YouTube Channel"
                value={formData.creator_data.youtube}
                onChange={(e) => handleInputChange('youtube', e.target.value, true)}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>@</Typography>
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Twitter/X Handle"
                value={formData.creator_data.twitter}
                onChange={(e) => handleInputChange('twitter', e.target.value, true)}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>@</Typography>
                }}
              />
            </Grid>
          </>
        )}
      </Grid>

      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={!usernameAvailable || loading}
        fullWidth
        size="large"
        startIcon={loading ? <CircularProgress size={20} /> : <PersonAddIcon />}
      >
        {loading ? 'Creating Account...' : 'Create Creator Account'}
      </Button>
    </Box>
  );
};

// User account creation form
const CreateUserForm = ({ onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    company_id: '',
    company_name: '',
    type: 'normal',
    first_name: '',
    last_name: '',
    phone_number: ''
  });

  const { data: companies } = useQuery(
    ['companies'],
    () => client.accountLinking.getCompanies(),
    {
      staleTime: 1000 * 60 * 10
    }
  );

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = () => {
    if (!formData.company_id || !formData.company_name || !formData.type) {
      return;
    }
    onSubmit(formData);
  };

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <FormControl fullWidth required>
            <InputLabel>Company</InputLabel>
            <Select
              value={formData.company_id}
              onChange={(e) => {
                const companyId = e.target.value;
                const company = companies?.data?.find(c => c.id === companyId);
                handleInputChange('company_id', companyId);
                handleInputChange('company_name', company?.name || '');
              }}
              label="Company"
            >
              {companies?.data?.map(company => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth required>
            <InputLabel>Account Type</InputLabel>
            <Select
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
              label="Account Type"
            >
              <MenuItem value="normal">Normal User</MenuItem>
              <MenuItem value="admin">Administrator</MenuItem>
              <MenuItem value="manager">Manager</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="First Name"
            value={formData.first_name}
            onChange={(e) => handleInputChange('first_name', e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Last Name"
            value={formData.last_name}
            onChange={(e) => handleInputChange('last_name', e.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Phone Number"
            type="tel"
            value={formData.phone_number}
            onChange={(e) => handleInputChange('phone_number', e.target.value)}
            placeholder="+1-555-0123"
          />
        </Grid>
      </Grid>

      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={!formData.company_id || !formData.company_name || !formData.type || loading}
        fullWidth
        size="large"
        startIcon={loading ? <CircularProgress size={20} /> : <BusinessIcon />}
      >
        {loading ? 'Creating Account...' : 'Create Business Account'}
      </Button>
    </Box>
  );
};

// Main wizard component
export default function AccountLinkingWizard() {
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:960px)');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { isAuthorized: isUserAuthorized, user: regularUser } = useAuth();
  const { isAuthorized: isCreatorAuthorized, user: creatorUser, isInitialized } = useCreatorAuth();

  const [currentStep, setCurrentStep] = useState(0);
  const [actionType, setActionType] = useState(null); // 'create-creator', 'link-creator', 'create-user', 'switch'

  // Account status query
  const { data: accountStatus, isLoading: statusLoading, refetch: refetchStatus } = useQuery(
    ['account-status'],
    () => client.accountLinking.getStatus(),
    {
      enabled: isUserAuthorized || isCreatorAuthorized,
      staleTime: 1000 * 60 * 5
    }
  );

  // Create creator mutation
  const createCreatorMutation = useMutation(
    (data) => client.accountLinking.createCreator(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['account-status']);
        setCurrentStep(0);
        setActionType(null);
      },
      onError: (error) => {
        replaceConsoleError('Failed to create creator account', error);
      }
    }
  );

  // Create user mutation
  const createUserMutation = useMutation(
    (data) => client.accountLinking.createUser(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['account-status']);
        setCurrentStep(0);
        setActionType(null);
      },
      onError: (error) => {
        replaceConsoleError('Failed to create user account', error);
      }
    }
  );

  // Switch context mutation
  const switchContextMutation = useMutation(
    (contextType) => client.accountLinking.switchContext(contextType),
    {
      onSuccess: (result) => {
        if (result.requires_new_token) {
          // Redirect to appropriate dashboard
          if (result.new_context === 'creator') {
            window.location.href = '/creatorconnect/start';
          } else {
            window.location.href = '/dashboard';
          }
        }
      },
      onError: (error) => {
        replaceConsoleError('Failed to switch context', error);
      }
    }
  );

  // Wait for creator auth to initialize
  if (!isInitialized) {
    return (
      <ThemeProvider theme={lightTheme}>
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4, md: 6 } }}>
          <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
            <Box textAlign="center">
              <LinearProgress sx={{ 
                width: { xs: 280, sm: 400 }, 
                mb: 3, 
                height: { xs: 6, sm: 8 }, 
                borderRadius: 4 
              }} />
              <Typography color="text.secondary" variant={isMobile ? "body1" : "h6"}>
                Initializing...
              </Typography>
            </Box>
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  if (!isUserAuthorized && !isCreatorAuthorized) {
    return (
      <ThemeProvider theme={lightTheme}>
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4, md: 6 } }}>
          <Box textAlign="center" py={{ xs: 6, sm: 10 }}>
            <AccountIcon sx={{ 
              fontSize: { xs: 60, sm: 80 }, 
              color: 'text.disabled', 
              mb: { xs: 2, sm: 3 } 
            }} />
            <Typography variant={isMobile ? "h5" : "h4"} color="text.secondary" gutterBottom>
              Please log in to manage your accounts
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/login')}
              sx={{ mt: 2 }}
            >
              Go to Login
            </Button>
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  if (statusLoading) {
    return (
      <ThemeProvider theme={lightTheme}>
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4, md: 6 } }}>
          <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
            <Box textAlign="center">
              <LinearProgress sx={{ 
                width: { xs: 280, sm: 400 }, 
                mb: 3, 
                height: { xs: 6, sm: 8 }, 
                borderRadius: 4 
              }} />
              <Typography color="text.secondary" variant={isMobile ? "body1" : "h6"}>
                Loading account information...
              </Typography>
            </Box>
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  const status = accountStatus?.data;

  const handleStartAction = (action) => {
    setActionType(action);
    setCurrentStep(1);
  };

  const handleBackToOverview = () => {
    setActionType(null);
    setCurrentStep(0);
  };

  const handleSwitchContext = (contextType) => {
    switchContextMutation.mutate(contextType);
  };

  // Render action-specific forms
  const renderActionForm = () => {
    switch (actionType) {
      case 'create-creator':
        return (
          <CreateCreatorForm
            onSubmit={(data) => createCreatorMutation.mutate(data)}
            loading={createCreatorMutation.isLoading}
          />
        );
      case 'create-user':
        return (
          <CreateUserForm
            onSubmit={(data) => createUserMutation.mutate(data)}
            loading={createUserMutation.isLoading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={lightTheme}>
      <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
        <Fade in timeout={600}>
          <Box textAlign="center" mb={{ xs: 4, sm: 6 }} px={{ xs: 1, sm: 0 }}>
            <Typography 
              variant={isMobile ? "h3" : isTablet ? "h2" : "h1"} 
              component="h1" 
              gutterBottom 
              display="flex" 
              alignItems="center" 
              justifyContent="center" 
              gap={{ xs: 2, sm: 3 }}
              sx={{
                fontWeight: 800,
                background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
                flexDirection: isMobile ? 'column' : 'row'
              }}
            >
              <SwitchIcon sx={{ 
                fontSize: { xs: 40, sm: 50, md: 60 }, 
                color: '#1976d2' 
              }} />
              Account Manager
            </Typography>
            <Typography 
              variant={isMobile ? "body1" : "h5"} 
              color="text.secondary" 
              sx={{ 
                maxWidth: { xs: '100%', sm: 600 }, 
                mx: 'auto',
                px: { xs: 2, sm: 0 }
              }}
            >
              Link your creator and business accounts for seamless switching
            </Typography>
          </Box>
        </Fade>

        {actionType ? (
          <Paper sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Button
                startIcon={<ArrowForwardIcon sx={{ transform: 'rotate(180deg)' }} />}
                onClick={handleBackToOverview}
                sx={{ mb: 2 }}
              >
                Back to Overview
              </Button>
              
              <Typography variant="h5" gutterBottom>
                {actionType === 'create-creator' ? 'Create Creator Account' : 'Create Business Account'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {actionType === 'create-creator' 
                  ? 'Set up your creator profile to start earning and collaborating' 
                  : 'Create a business account to manage campaigns and teams'
                }
              </Typography>
            </Box>

            {renderActionForm()}
          </Paper>
        ) : (
          <Paper sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
            {/* Current Account Status */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom display="flex" alignItems="center" gap={2}>
                <AccountIcon />
                Current Account Status
              </Typography>

              <Grid container spacing={3}>
                {/* Business Account Card */}
                <Grid item xs={12} md={6}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      height: '100%',
                      border: status?.has_user_account ? '2px solid #4caf50' : '1px solid #e0e0e0',
                      backgroundColor: status?.has_user_account ? '#f1f8e9' : '#fafafa'
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <BusinessIcon color={status?.has_user_account ? 'success' : 'disabled'} />
                        <Typography variant="h6">Business Account</Typography>
                        {status?.has_user_account && <CheckIcon color="success" />}
                      </Box>
                      
                      {status?.has_user_account ? (
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Name:</strong> {status.user_info?.first_name} {status.user_info?.last_name}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Company:</strong> {status.user_info?.company_name}
                          </Typography>
                          {status.current_context === 'user' && (
                            <Chip label="Currently Active" color="primary" size="small" />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No business account linked
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Creator Account Card */}
                <Grid item xs={12} md={6}>
                  <Card 
                    variant="outlined"
                    sx={{ 
                      height: '100%',
                      border: status?.has_creator_account ? '2px solid #9c27b0' : '1px solid #e0e0e0',
                      backgroundColor: status?.has_creator_account ? '#f3e5f5' : '#fafafa'
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <StarIcon color={status?.has_creator_account ? 'secondary' : 'disabled'} />
                        <Typography variant="h6">Creator Account</Typography>
                        {status?.has_creator_account && <CheckIcon sx={{ color: '#9c27b0' }} />}
                      </Box>
                      
                      {status?.has_creator_account ? (
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Handle:</strong> @{status.creator_info?.username}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Display Name:</strong> {status.creator_info?.display_name}
                          </Typography>
                          {status.current_context === 'creator' && (
                            <Chip label="Currently Active" sx={{ backgroundColor: '#9c27b0', color: 'white' }} size="small" />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No creator account linked
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Available Actions */}
            <Box>
              <Typography variant="h5" gutterBottom display="flex" alignItems="center" gap={2}>
                <TrendingUpIcon />
                Available Actions
              </Typography>

              <Grid container spacing={2}>
                {/* Context Switching */}
                {status?.has_user_account && status?.has_creator_account && (
                  <>
                    {status.current_context !== 'normal' && (
                      <Grid item xs={12} sm={6}>
                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={<BusinessIcon />}
                          onClick={() => handleSwitchContext('normal')}
                          disabled={switchContextMutation.isLoading}
                          size="large"
                        >
                          Switch to Business
                        </Button>
                      </Grid>
                    )}
                    {status.current_context !== 'creator' && (
                      <Grid item xs={12} sm={6}>
                        <Button
                          variant="contained"
                          color="secondary"
                          fullWidth
                          startIcon={<StarIcon />}
                          onClick={() => handleSwitchContext('creator')}
                          disabled={switchContextMutation.isLoading}
                          size="large"
                        >
                          Switch to Creator
                        </Button>
                      </Grid>
                    )}
                  </>
                )}

                {/* Create Missing Accounts */}
                {status?.can_create_creator && (
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      fullWidth
                      startIcon={<PersonAddIcon />}
                      onClick={() => handleStartAction('create-creator')}
                      size="large"
                    >
                      Create Creator Account
                    </Button>
                  </Grid>
                )}

                {status?.can_create_user && (
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<BusinessIcon />}
                      onClick={() => handleStartAction('create-user')}
                      size="large"
                    >
                      Create Business Account
                    </Button>
                  </Grid>
                )}

                {/* Dashboard Links */}
                {status?.has_user_account && (
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="text"
                      fullWidth
                      startIcon={<ViewIcon />}
                      onClick={() => window.location.href = '/dashboard'}
                      size="large"
                    >
                      View Business Dashboard
                    </Button>
                  </Grid>
                )}

                {status?.has_creator_account && (
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="text"
                      fullWidth
                      startIcon={<ViewIcon />}
                      onClick={() => window.location.href = '/creatorconnect/start'}
                      size="large"
                    >
                      View Creator Dashboard
                    </Button>
                  </Grid>
                )}
              </Grid>

              {/* Success Messages */}
              {createCreatorMutation.isSuccess && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Creator account created successfully! You can now switch between accounts.
                </Alert>
              )}

              {createUserMutation.isSuccess && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Business account created successfully! You can now switch between accounts.
                </Alert>
              )}

              {/* Error Messages */}
              {(createCreatorMutation.isError || createUserMutation.isError) && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {createCreatorMutation.error?.response?.data?.error || 
                   createUserMutation.error?.response?.data?.error || 
                   'Failed to create account. Please try again.'}
                </Alert>
              )}
            </Box>
          </Paper>
        )}
      </Container>
    </ThemeProvider>
  );
}